import { BaseButton, Column, Divider, Padding, Row, SizedBox } from "../CommonBaseComponents";
import CustomIcon from "../image/CustomIcon";
import { C4Text } from "../text/CustomText";
import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { useClickOutside } from "../../hooks/useClickOutside";
import CalendarComponent from "../calendar/CalendarComponent";
import ParseUtil from "../../utils/ParseUtil";
import { CalendarWrapper, DateInput } from "../calendar/CalendarMenu";
import DateFilterDropdownMenu from "../dropdown/DateFilterDropdownMenu";
import { Theme } from "../../Theme";

const MenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

function Calendar({ range, setRange, openedLeftSide }) {
    const [selectedType, setSelectedType] = useState("day");

    function handleCaptionClick() {
        if (selectedType === "day") {
            setSelectedType("month");
        } else if (selectedType === "month") {
            setSelectedType("year");
        }
    }

    return (
        <CalendarWrapper openedCalendarLeftSide={openedLeftSide} $leftPositon={"-480px"} $notLeftPositon={"250px"}>
            <CalendarComponent
                range={range}
                setRange={setRange}
                type={selectedType}
                onCaptionClick={handleCaptionClick}
                setType={setSelectedType}
            />
            {selectedType === "day" && (
                <Row width={"100%"} justifyContent={"space-between"} padding={"0 4px"}>
                    <Column width={"50%"} justifyContent={"flex-start"} alignItem={"flex-start"} padding={"0 5.5px"}>
                        <Padding padding={"0 8px"}>
                            <C4Text fontColor={Theme.color.gray500}>From</C4Text>
                        </Padding>
                        <SizedBox height={"4px"} />
                        <DateInput name={"from"} value={range?.from ? format(range.from, "yyyy-MM-dd") : ""} />
                    </Column>
                    <Column width={"50%"} justifyContent={"flex-start"} alignItem={"flex-start"} padding={"0 5.5px"}>
                        <Padding padding={"0 8px"}>
                            <C4Text fontColor={Theme.color.gray500}>To</C4Text>
                        </Padding>
                        <SizedBox height={"4px"} />
                        <DateInput name={"to"} value={range?.to ? format(range.to, "yyyy-MM-dd") : ""} />
                    </Column>
                </Row>
            )}
        </CalendarWrapper>
    );
}

export function CustomDateFilterButton({
    currentValue,
    setValue,
    values = ["All Date", "Today", "7 Days", "30 Days", "From - To"],
    openedLeftSide = false,
}) {
    const firstValue = values && values.length > 0 ? values[0] : null;

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [dateRange, setDateRange] = useState("");
    const [range, setRange] = useState();

    useEffect(() => {
        if (!values.includes("From - To")) {
            setDateRange(currentValue);
            return;
        }

        const toDate = new Date();

        let fromDate = new Date();
        let rangeString = "";

        switch (currentValue) {
            case "All Date":
                rangeString = "All Date";
                break;

            case "Today":
                // 어제 ~ 오늘
                fromDate.setDate(toDate.getDate() - 1);
                rangeString = ParseUtil.parseDateToString(fromDate) + " - " + ParseUtil.parseDateToString(toDate);
                break;

            case "7 Days":
                fromDate.setDate(toDate.getDate() - 6);
                rangeString = ParseUtil.parseDateToString(fromDate) + " - " + ParseUtil.parseDateToString(toDate);
                break;

            case "30 Days":
                fromDate.setDate(toDate.getDate() - 29);
                rangeString = ParseUtil.parseDateToString(fromDate) + " - " + ParseUtil.parseDateToString(toDate);
                break;

            default:
                rangeString = currentValue;
                break;
        }

        setDateRange(rangeString);
    }, [currentValue, values]);

    const toggleMenu = () => {
        setIsMenuOpen((prev) => !prev);
        setIsCalendarOpen(false);
    };

    const menuRef = useRef(null);
    useClickOutside(menuRef, () => {
        if (isMenuOpen) {
            setIsMenuOpen(false);
        }
        if (isCalendarOpen) {
            setIsCalendarOpen(false);
        }
    });

    const setCustomValue = () => {
        setValue(
            (range?.from ? format(range.from, "yyyy-MM-dd") : "") +
                " - " +
                (range?.to ? format(range.to, "yyyy-MM-dd") : "")
        );
    };

    return (
        <MenuContainer ref={menuRef}>
            <BaseButton
                padding={"9px 10px"}
                border={
                    currentValue === firstValue && !isMenuOpen
                        ? `1px solid ${Theme.color.gray250}`
                        : `1px solid ${Theme.color.indigo500}`
                }
                borderRadius={"8px"}
                backgroundColor={
                    currentValue === firstValue && !isMenuOpen ? Theme.color.white : Theme.color.indigo500_005
                }
                hoverBackgroundColor={
                    currentValue === firstValue && !isCalendarOpen ? Theme.color.neutral100 : Theme.color.indigo500_01
                }
            >
                <Row alignItem={"center"} cursor={"pointer"}>
                    <Row alignItem={"flex-start"} cursor={"pointer"} onClick={toggleMenu}>
                        <CustomIcon
                            iconName={currentValue === firstValue ? "date" : "date_active"}
                            width={"13.5px"}
                            height={"12px"}
                        />
                        <SizedBox width={"7px"} />
                        <C4Text fontColor={currentValue === firstValue ? Theme.color.zinc850 : Theme.color.indigo500}>
                            {dateRange}
                        </C4Text>
                    </Row>
                    <SizedBox width={"4px"} onClick={toggleMenu} />
                    <CustomIcon
                        iconName={isMenuOpen ? "chevron_up" : "chevron_down"}
                        isActive={currentValue !== firstValue}
                        size={"16px"}
                        cursor={"pointer"}
                        onClick={toggleMenu}
                    />
                    {currentValue !== firstValue && (
                        <>
                            <SizedBox width={"7px"} />
                            <Divider orientation={"vertical"} color={Theme.color.indigo500} height={"15px"} />
                            <SizedBox width={"7px"} />
                            <CustomIcon
                                iconName={"close_active"}
                                size={"16px"}
                                cursor={"pointer"}
                                onClick={() => setValue(firstValue)}
                            />
                        </>
                    )}
                </Row>
            </BaseButton>
            {isMenuOpen && (
                <DateFilterDropdownMenu
                    initValue={currentValue}
                    setValue={setValue}
                    values={values}
                    onClose={toggleMenu}
                    setIsCalendarOpen={setIsCalendarOpen}
                    setCustomValue={setCustomValue}
                    openedLeftSide={openedLeftSide}
                />
            )}
            {isCalendarOpen && <Calendar range={range} setRange={setRange} openedLeftSide={openedLeftSide} />}
        </MenuContainer>
    );
}
